import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { deleteContacts } from '@disco/state-management/actions/contact-actions'
import { updateUserSettingsUnsafe } from '@disco/state-management/actions/ExplorerActions'
import {
  getSelectedContacts,
  makeGetContact,
} from '@disco/state-management/selectors/contacts'
import { getHideContactRemovalSetting } from '@disco/state-management/selectors/user'
import DocumentHeadTags from 'components/document-head-tags'
import WarningDialog from 'components/modals/dialogs/warning-dialog'
import type { RootState } from 'store/configureStore'
interface OwnProps {
  params?: {
    contactId?: string
  }
  navigate: NavigateFunction
}

function mapStateToProps(
  state: RootState,
  { params: { contactId } }: OwnProps,
) {
  const getContact = makeGetContact()
  return {
    contacts: contactId
      ? [
          getContact(state, {
            contactId: Number.parseInt(contactId, 10),
          }),
        ]
      : getSelectedContacts(state),
    hideRemovalWarning: getHideContactRemovalSetting(state),
    settings: state.user.settings,
  }
}

const mapDispatchToProps = {
  deleteContacts,
  updateUserSettingsUnsafe,
  sendMixpanelEvent,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux & OwnProps

class ContactDelete extends Component<Props> {
  _onClose = () => {
    if (this.props.params?.contactId) {
      this.props.navigate(`/contacts/${this.props.params.contactId}`)
    } else {
      this.props.navigate('/contacts')
    }
  }
  _handleDelete = () => {
    this.props.deleteContacts(this.props.contacts.map(c => c.id))
    this.props.navigate('/contacts')
  }
  _hideWarning = () => {
    this.props.updateUserSettingsUnsafe({
      settings: {
        hide_contact_removal_warning: !this.props.hideRemovalWarning,
      },
    })
  }

  render() {
    const singleContact = this.props.contacts.length === 1
    const message = singleContact
      ? 'Are you sure you want to delete this contact?'
      : 'Are you sure you want to delete these contacts?'
    const title = singleContact ? 'Delete contact' : 'Delete contacts'
    return (
      <div>
        <DocumentHeadTags title={`${title} | DISCO`} />
        <WarningDialog
          confirmButtonCaption="Delete"
          onConfirm={this._handleDelete}
          onCancel={this._onClose}
          onHideWarningChange={this._hideWarning}
          hideWarning={this.props.hideRemovalWarning}
        >
          {message}
        </WarningDialog>
      </div>
    )
  }
}

// Higher-order component to provide navigate function and params to class component
const withNavigate = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate()
    const params = useParams<{ contactId?: string }>()
    return <Component {...props} navigate={navigate} params={params} />
  }
}

const ConnectedContactDelete = connector(ContactDelete)

export default withNavigate(ConnectedContactDelete)
